/**
 * Receitas Page
 * Mobile-first page for tracking revenue entries with hero-style value input
 * Design follows BudgetPage pattern - clean, flat, no excessive rounded boxes
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../store/uiStore';
import {
  useRevenueEntries,
  useCreateRevenueEntry,
  useUpdateRevenueEntry,
  useDeleteRevenueEntry,
  type RevenueEntryRecord,
} from '../hooks/useRevenueEntries';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { EditRevenueModal } from '../components/ui/EditRevenueModal';
import { DeleteConfirmDialog } from '../components/ui/DeleteConfirmDialog';
import { CurrencyButton } from '../components/ui/CurrencyButton';
import { type SupportedCurrency, CURRENCY_SYMBOLS } from '../types';
import {
  formatDateDisplay,
  dateInputToISOString,
} from '../utils/dateUtils';

// Month translation keys
const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

// Format number as Brazilian currency
const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Convert BRL amount back to original entry currency using stored snapshot
const getOriginalAmount = (
  amountBRL: number,
  currency: string,
  currencySnapshots: Record<string, number>
): number => {
  const rate = currencySnapshots?.[currency];
  if (!rate || rate === 0 || currency === 'BRL') {
    return amountBRL;
  }
  return amountBRL * rate;
};

// Get today's date formatted for input (YYYY-MM-DD)
const getTodayForInput = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Action Menu Component
function ActionMenu({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  isDark,
}: {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`absolute right-0 top-full mt-1 w-32 rounded-lg shadow-lg z-10 overflow-hidden ${
        isDark ? 'bg-slate-700 border border-slate-600' : 'bg-white border border-slate-200'
      }`}
      data-testid="action-menu"
    >
      <button
        onClick={onEdit}
        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
          isDark ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-100'
        }`}
        data-testid="edit-action-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        {t('common.edit')}
      </button>
      <button
        onClick={onDelete}
        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
          isDark ? 'text-red-400 hover:bg-slate-600' : 'text-red-600 hover:bg-red-50'
        }`}
        data-testid="delete-action-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        {t('common.delete')}
      </button>
    </div>
  );
}

export function ReceitasPage() {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const { preferences } = useUserPreferences();

  // Get translated month names
  const monthNames = MONTH_KEYS.map(key => t(`months.${key}`));

  // Current month state
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Form state
  const [amount, setAmount] = useState(0);
  const [displayAmount, setDisplayAmount] = useState('0,00');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(getTodayForInput());
  const [showSuccess, setShowSuccess] = useState(false);

  // Currency state - default to user's preferred currency
  const defaultCurrency = (preferences?.defaultCurrency as SupportedCurrency) || 'BRL';
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(defaultCurrency);

  // Update currency when preferences load
  useEffect(() => {
    if (preferences?.defaultCurrency) {
      setSelectedCurrency(preferences.defaultCurrency as SupportedCurrency);
    }
  }, [preferences?.defaultCurrency]);

  // Ref for amount input focus
  const amountInputRef = useRef<HTMLInputElement>(null);

  // Focus amount input on page load
  useEffect(() => {
    amountInputRef.current?.focus();
  }, []);

  // Action menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Edit modal state
  const [editingEntry, setEditingEntry] = useState<RevenueEntryRecord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete dialog state
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Hooks - pass defaultCurrency to get total in user's preferred currency
  // Amounts are stored in BRL and converted using currencySnapshots from each entry
  const { entries, total, isLoading } = useRevenueEntries(currentYear, currentMonth, defaultCurrency);
  const createEntryMutation = useCreateRevenueEntry();
  const updateEntryMutation = useUpdateRevenueEntry();
  const deleteEntryMutation = useDeleteRevenueEntry();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseFloat(raw) / 100 || 0;
    setAmount(numValue);
    setDisplayAmount(formatCurrency(numValue));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (amount <= 0 || !description.trim()) {
      return;
    }

    createEntryMutation.mutate(
      {
        amount,
        description: description.trim(),
        date: dateInputToISOString(date),
        currency: selectedCurrency,
      },
      {
        onSuccess: () => {
          // Reset form
          setAmount(0);
          setDisplayAmount('0,00');
          setDescription('');
          setDate(getTodayForInput());
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 2000);
        },
      }
    );
  };

  const handleEditClick = (entry: RevenueEntryRecord) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteClick = (entryId: string) => {
    setDeletingEntryId(entryId);
    setIsDeleteDialogOpen(true);
    setOpenMenuId(null);
  };

  const handleEditSave = (data: { amount: number; description: string; date: string }) => {
    if (!editingEntry) return;

    updateEntryMutation.mutate(
      {
        entryId: editingEntry.id,
        data,
      },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deletingEntryId) return;

    deleteEntryMutation.mutate(deletingEntryId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingEntryId(null);
      },
    });
  };

  const isFormValid = amount > 0 && description.trim().length > 0;
  const isSaving = createEntryMutation.isPending;

  return (
    <div className="px-4 py-6 space-y-8">
      {/* Total Card */}
      <div className={`rounded-2xl p-4 ${isDark ? 'bg-emerald-900/20 border border-emerald-800/50' : 'bg-emerald-50 border border-emerald-200'}`}>
        <div className="flex items-center gap-3">
          {/* Dollar Icon */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center animate-single-flip ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
            <svg className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className={`text-sm font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
            {t('pages.receitas.monthTotal')}
          </span>
          <span
            className={`text-xl font-bold ml-auto ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
            data-testid="receitas-total"
          >
            {CURRENCY_SYMBOLS[defaultCurrency]} {formatCurrency(total)}
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="text-center">
        <h2 className={`text-xl font-semibold mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          {t('pages.receitas.addIncome')}
        </h2>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {t('pages.receitas.incomeValue')}
        </p>
      </div>

      {/* Hero Amount Input */}
      <div className="text-center">
        <div className="inline-flex items-baseline gap-1">
          <CurrencyButton
            currency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            size="md"
            variant="income"
          />
          <input
            ref={amountInputRef}
            type="text"
            inputMode="decimal"
            value={displayAmount}
            onChange={handleAmountChange}
            data-testid="receita-amount-input"
            className="text-5xl font-bold text-emerald-500 bg-transparent border-none outline-none text-center w-48"
            placeholder="0,00"
          />
        </div>
      </div>

      {/* Description Input */}
      <div>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('pages.receitas.descriptionOptional')}
          data-testid="receita-description-input"
          className="input text-center"
        />
      </div>

      {/* Date Input */}
      <div>
        <label className={`text-sm font-medium mb-2 block ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('pages.receitas.date')}
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="receita-date-input"
          className="input"
        />
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isFormValid || isSaving}
        data-testid="add-receita-button"
        className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
          isFormValid && !isSaving
            ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
            : isDark
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
        }`}
      >
        {isSaving ? t('common.saving') : t('pages.receitas.addIncome')}
      </button>

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed bottom-24 left-4 right-4 bg-emerald-900/90 border border-emerald-700 text-emerald-100 px-4 py-3 rounded-lg text-center animate-slide-up">
          {t('pages.receitas.addSuccess')}
        </div>
      )}

      {/* History Section */}
      <div className="pt-4 border-t border-slate-700/30">
        <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          {t('pages.receitas.history')} ({monthNames[currentMonth - 1]})
        </h2>

        {isLoading ? (
          <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('common.loading')}
          </div>
        ) : entries.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('pages.receitas.noIncome')}
          </div>
        ) : (
          <div className="space-y-2" data-testid="receitas-list">
            {entries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`receita-item-${entry.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg relative ${
                  isDark ? 'bg-slate-800/50' : 'bg-white border border-slate-200'
                }`}
              >
                {/* Green Up Arrow Icon */}
                <svg className={`w-5 h-5 shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                    data-testid={`receita-description-${entry.id}`}
                  >
                    {entry.description}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {formatDateDisplay(entry.date)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                    data-testid={`receita-amount-${entry.id}`}
                  >
                    {CURRENCY_SYMBOLS[entry.currency as SupportedCurrency] || 'R$'} {formatCurrency(getOriginalAmount(entry.amount, entry.currency, entry.currencySnapshots))}
                  </span>
                  {/* Action Menu Button */}
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === entry.id ? null : entry.id)}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                      }`}
                      data-testid={`action-menu-button-${entry.id}`}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="6" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="18" r="1.5" />
                      </svg>
                    </button>
                    <ActionMenu
                      isOpen={openMenuId === entry.id}
                      onClose={() => setOpenMenuId(null)}
                      onEdit={() => handleEditClick(entry)}
                      onDelete={() => handleDeleteClick(entry.id)}
                      isDark={isDark}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditRevenueModal
        entry={editingEntry}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleEditSave}
        isPending={updateEntryMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setDeletingEntryId(null);
        }}
        onConfirm={handleDeleteConfirm}
        isPending={deleteEntryMutation.isPending}
        title={t('modals.deleteConfirm.incomeTitle')}
        message={t('modals.deleteConfirm.incomeMessage')}
      />
    </div>
  );
}
